import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { isActiveCollaborator } from '@/lib/employment';
import { Loader2, Rocket, ArrowRight } from 'lucide-react';

// Suites with dedicated self-service flows — the generic launcher routes there.
const SUITE_ROUTES = {
  talentcheck: '/client/talentcheck',
  talentcompetency: '/client/talentcompetency',
  talenttrain: '/client/talenttrain',
  climara: '/client/climara',
};
const SUITE_NAMES = {
  talentmap: 'TalentMap', talentcheck: 'TalentCheck', talentcompetency: 'TalentCompetency',
  talenttrain: 'TalentTrain', climara: 'Climara', talentexit: 'TalentExit',
  talentperformance: 'TalentPerformance', succession: 'Succession',
};
// Suites the generic backend endpoint accepts for direct assignment.
const DIRECT_SUITES = ['talentmap'];

/**
 * Client Autonomy — shared SELF-SERVICE launch dialog. Assigns an already
 * PUBLISHED Library instrument directly to collaborators via
 * POST /client/evaluations/assign (reuses the existing assignment engine).
 * Instruments remain CTH-governed; no approval request is created.
 */
export function ClientSelfServeAssignDialog({ open, onOpenChange, suite = 'talentmap', presetInstrument = null, presetUserIds = null, allowSuitePick = false, onCreated }) {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').toLowerCase().startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const navigate = useNavigate();

  const [suiteSel, setSuiteSel] = useState(suite || 'talentmap');
  const [contracted, setContracted] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [instrumentId, setInstrumentId] = useState(presetInstrument?.id || '');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState('');
  const [deadline, setDeadline] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const routed = SUITE_ROUTES[suiteSel];
  const direct = DIRECT_SUITES.includes(suiteSel);

  useEffect(() => {
    if (!open) return;
    setSuiteSel(suite || 'talentmap');
    setInstrumentId(presetInstrument?.id || '');
    setSelected([]); setNotes(''); setDeadline('');
    setLoading(true);
    Promise.all([
      api.get('/client/users').then((r) => r.data).catch(() => []),
      allowSuitePick ? api.get('/client/me/company').then((r) => r.data).catch(() => null) : Promise.resolve(null),
    ]).then(([u, co]) => {
      setUsers((u || []).filter(isActiveCollaborator));
      // Legacy-request continuation — preselect the originally requested people.
      if (presetUserIds && presetUserIds.length) {
        const valid = new Set((u || []).filter(isActiveCollaborator).map((x) => x.id));
        setSelected(presetUserIds.filter((id) => valid.has(id)));
      }
      if (co) setContracted(co.suite_codes || []);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (!open || !direct) { setInstruments([]); return; }
    api.get('/client/library', { params: { suite: suiteSel } })
      .then((r) => setInstruments(r.data || []))
      .catch(() => setInstruments([]));
    // eslint-disable-next-line
  }, [open, suiteSel]);

  const suiteOptions = useMemo(() => {
    const known = [...DIRECT_SUITES, ...Object.keys(SUITE_ROUTES)];
    return known.filter((s) => contracted.includes(s));
  }, [contracted]);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // CTH-030 — collaborator search + select-all-filtered with accurate count.
  const [userQ, setUserQ] = useState('');
  const visibleUsers = users.filter((u) => !userQ.trim()
    || (u.name || '').toLowerCase().includes(userQ.toLowerCase())
    || (u.email || '').toLowerCase().includes(userQ.toLowerCase())
    || (u.position || '').toLowerCase().includes(userQ.toLowerCase()));
  const allFilteredSelected = visibleUsers.length > 0 && visibleUsers.every((u) => selected.includes(u.id));
  const selectAllFiltered = () => {
    const ids = visibleUsers.map((u) => u.id);
    setSelected((p) => allFilteredSelected
      ? p.filter((x) => !ids.includes(x))
      : [...new Set([...p, ...ids])]);
  };

  const submit = async () => {
    if (!instrumentId) return toast.error(L('Selecciona una evaluación publicada.', 'Select a published assessment.'));
    if (!selected.length) return toast.error(L('Selecciona al menos un colaborador.', 'Select at least one collaborator.'));
    setSubmitting(true);
    try {
      const r = await api.post('/client/evaluations/assign', {
        suite_code: suiteSel,
        questionnaire_id: instrumentId,
        collaborator_ids: selected,
        deadline: deadline || null,
        context_notes: notes.trim() || null,
      });
      const names = users.filter((u) => selected.includes(u.id)).map((u) => u.name).join(', ');
      toast.success(L(
        `Evaluación asignada correctamente. Ya está disponible en Mi Portal de ${names || `${r.data.count} colaborador(es)`}.`,
        `Evaluation assigned successfully. It is now available in ${names || `${r.data.count} collaborator(s)`}'s Mi Portal.`,
      ));
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ssa-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            {L('Lanzar evaluación (autogestión)', 'Launch evaluation (self-service)')}
          </DialogTitle>
          <DialogDescription>
            {L('Asigna directamente una evaluación publicada a tus colaboradores. Los instrumentos y su calificación permanecen gobernados por el equipo CTH; esta asignación no requiere aprobación.',
               'Directly assign a published assessment to your collaborators. Instruments and scoring remain governed by the CTH team; this assignment requires no approval.')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            {allowSuitePick && (
              <div className="space-y-1.5">
                <Label>{L('Suite', 'Suite')}</Label>
                <Select value={suiteSel} onValueChange={setSuiteSel}>
                  <SelectTrigger data-testid="ssa-suite-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {suiteOptions.map((s) => (
                      <SelectItem key={s} value={s}>{SUITE_NAMES[s] || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!direct && routed ? (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3" data-testid="ssa-route-hint">
                <p className="text-sm">
                  {L(`${SUITE_NAMES[suiteSel] || suiteSel} tiene su propio flujo de autogestión con la configuración específica del módulo.`,
                     `${SUITE_NAMES[suiteSel] || suiteSel} has its own self-service flow with the module-specific configuration.`)}
                </p>
                <Button size="sm" className="gap-1.5" data-testid="ssa-goto-module"
                        onClick={() => { onOpenChange(false); navigate(routed); }}>
                  {L('Ir al módulo', 'Go to module')} <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label>{L('Evaluación publicada', 'Published assessment')}</Label>
                  {presetInstrument ? (
                    <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium" data-testid="ssa-instrument-locked">
                      {isES ? (presetInstrument.name_es || presetInstrument.name) : presetInstrument.name}
                    </div>
                  ) : (
                    <Select value={instrumentId} onValueChange={setInstrumentId}>
                      <SelectTrigger data-testid="ssa-instrument-select">
                        <SelectValue placeholder={L('Selecciona una evaluación…', 'Select an assessment…')} />
                      </SelectTrigger>
                      <SelectContent>
                        {instruments.map((q) => (
                          <SelectItem key={q.id} value={q.id}>{isES ? (q.name_es || q.name) : q.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {!instruments.length && !presetInstrument && (
                    <p className="text-xs text-muted-foreground italic">
                      {L('No hay evaluaciones publicadas disponibles para esta suite.', 'No published assessments available for this suite.')}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>{L('Colaboradores', 'Collaborators')} ({selected.length})</Label>
                  <div className="flex items-center gap-2">
                    <Input value={userQ} onChange={(e) => setUserQ(e.target.value)} className="h-8"
                           placeholder={L('Buscar colaborador…', 'Search collaborator…')} data-testid="ssa-collab-search" />
                    <Button type="button" size="sm" variant="outline" className="h-8 shrink-0 text-xs"
                            onClick={selectAllFiltered} disabled={!visibleUsers.length}
                            data-testid="ssa-select-all">
                      {allFilteredSelected
                        ? L('Quitar selección', 'Clear selection')
                        : L(`Seleccionar todos (${visibleUsers.length})`, `Select all (${visibleUsers.length})`)}
                    </Button>
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-md border divide-y" data-testid="ssa-collaborators">
                    {visibleUsers.map((u) => (
                      <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-muted/40">
                        <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)}
                                  data-testid={`ssa-collab-${u.id}`} />
                        <span className="flex-1 min-w-0 truncate">{u.name}</span>
                        <span className="text-xs text-muted-foreground truncate">{u.position || ''}</span>
                      </label>
                    ))}
                    {!users.length && (
                      <p className="px-3 py-3 text-xs text-muted-foreground italic">
                        {L('No hay colaboradores elegibles.', 'No eligible collaborators.')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{L('Fecha límite (opcional)', 'Deadline (optional)')}</Label>
                    <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                           className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                           data-testid="ssa-deadline" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>{L('Notas de contexto (opcional)', 'Context notes (optional)')}</Label>
                  <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ssa-notes" />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ssa-cancel">
                    {L('Cancelar', 'Cancel')}
                  </Button>
                  <Button onClick={submit} disabled={submitting} className="gap-1.5" data-testid="ssa-submit">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                    {L('Asignar evaluación', 'Assign evaluation')}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ClientSelfServeAssignDialog;
